import { useEffect, useRef, useState, type ReactNode } from 'react';
import { formatDuration } from '../lib/dates.js';
import { vibrate } from '../platform/capabilities.js';
import { pitidoDeAviso } from '../platform/sonido.js';
import './escala.css';

/**
 * Cuanto puede retrasarse el `tick` del cero y seguir mereciendo un aviso.
 *
 * Un segundo cubre el intervalo de 250 ms y cualquier tiron normal del hilo.
 * Mas alla, la pantalla ha estado dormida y el aviso llega fuera de tiempo.
 */
const TOLERANCIA_DE_AVISO_S = 1;

/**
 * Temporizador de descanso.
 *
 * Arranca solo al confirmar una serie y avisa al terminar. NO hay controles
 * para aumentar o reducir el descanso a mano (requisito §10.2): si el usuario
 * quiere seguir antes, simplemente registra la serie siguiente.
 */
export function Descanso({
  segundos,
  iniciadoEn,
  onFin,
}: {
  segundos: number;
  /** Marca temporal (ms) en la que arrancó el descanso. */
  iniciadoEn: number;
  onFin?: (transcurridos: number) => void;
}): ReactNode {
  const [restantes, setRestantes] = useState(() =>
    Math.max(0, segundos - Math.floor((Date.now() - iniciadoEn) / 1000)),
  );
  const avisado = useRef(false);

  useEffect(() => {
    avisado.current = false;
    const tick = (): void => {
      const transcurridos = Math.floor((Date.now() - iniciadoEn) / 1000);
      const quedan = Math.max(0, segundos - transcurridos);
      setRestantes(quedan);

      if (quedan === 0 && !avisado.current) {
        avisado.current = true;

        /*
         * EL AVISO SOLO SI SEGUIAMOS MIRANDO.
         *
         * Este `tick` corre en un `setInterval`, y un `setInterval` no corre
         * con la pantalla apagada ni con la aplicacion en segundo plano:
         * Android lo frena o lo suspende. Al volver, el primer `tick` ve cero
         * y dispara — pero pueden haber pasado cinco minutos, y entonces el
         * usuario ya esta a mitad de la serie siguiente. Un pitido y una
         * vibracion larga ahi no avisan de nada, sobresaltan.
         *
         * Quien avisa en ese caso es la notificacion PROGRAMADA, que la
         * entrega el sistema a su hora aunque el movil este en el bolsillo.
         * Aqui solo se avisa si el cero se ha alcanzado ahora mismo.
         *
         * `onFin` se llama igual siempre: registrar el descanso no depende de
         * que nadie estuviera delante.
         */
        const retraso = transcurridos - segundos;
        if (retraso <= TOLERANCIA_DE_AVISO_S) {
          // `alarma`, no `heavy`: `heavy` es un pulso de 60 ms —el golpecito de
          // confirmar un boton— y con el movil en el bolsillo no se nota.
          void vibrate('alarma');
          pitidoDeAviso();
        }

        onFin?.(transcurridos);
      }
    };

    tick();
    const interval = globalThis.setInterval(tick, 250);
    return () => globalThis.clearInterval(interval);
  }, [segundos, iniciadoEn, onFin]);

  const progreso = segundos === 0 ? 100 : ((segundos - restantes) / segundos) * 100;
  const terminado = restantes === 0;

  return (
    <div
      className={`descanso${terminado ? ' descanso--fin' : ''}`}
      role="timer"
      aria-live="off"
    >
      {/* La cuenta y su rotulo en la MISMA linea, en los dos extremos: apilados
          en una esquina, la barra quedaba al lado en una columna estrecha y
          medía tres centimetros. A lo ancho, la barra es la referencia visual
          que se lee de reojo desde el banco. */}
      <div className="descanso__fila">
        <p className="descanso__cuenta">{terminado ? '¡Vamos!' : formatDuration(restantes)}</p>
        <p className="descanso__etiqueta">
          {terminado ? 'Descanso terminado' : `Descanso de ${formatDuration(segundos)}`}
        </p>
      </div>
      <div className="descanso__barra" aria-hidden="true">
        <div className="descanso__progreso" style={{ width: `${progreso}%` }} />
      </div>
    </div>
  );
}
